import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Stack, Chip, CircularProgress,
    Input, IconButton,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon           from '@mui/icons-material/ArrowBackRounded';
import GavelRoundedIcon               from '@mui/icons-material/GavelRounded';
import BlockRoundedIcon               from '@mui/icons-material/BlockRounded';
import MoneyOffRoundedIcon            from '@mui/icons-material/MoneyOffRounded';
import CalendarTodayRoundedIcon       from '@mui/icons-material/CalendarTodayRounded';
import AdminPanelSettingsRoundedIcon  from '@mui/icons-material/AdminPanelSettingsRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetFines } from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', weekday: 'short' });
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getPeriods = () => {
    const now = new Date();
    const today = toISO(now);
    const weekStart = new Date(now);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return [
        { label: 'Сегодня', from: today, to: today },
        { label: 'Вчера',   from: toISO(yesterday), to: toISO(yesterday) },
        { label: 'Неделя',  from: toISO(weekStart), to: today },
        { label: 'Месяц',   from: toISO(monthStart), to: today },
    ];
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };
const fadeUp  = { hidden: { opacity: 0, y: 14 }, visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } } };

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    "&:hover": {
        bgcolor: 'var(--joy-palette-background-level3)', 
    }
};


// ════════════════════════════════════════════════════════════════════
//  MyFinesPage (фотограф — только просмотр)
// ════════════════════════════════════════════════════════════════════
export default function MyFinesPage() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const periods = useMemo(getPeriods, []);

    const [fines,        setFines]        = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [dateFrom,     setDateFrom]     = useState(getMonthStart);
    const [dateTo,       setDateTo]       = useState(() => toISO(new Date()));
    const [activePeriod, setActivePeriod] = useState('Месяц');

    // ── Загрузка (бэкенд автоматически фильтрует по текущему пользователю) ──
    const loadFines = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;

        const data = await APIGetFines(token, params);
        if (Array.isArray(data)) setFines(data);
        setLoading(false);
    }, [token, dateFrom, dateTo]);

    useEffect(() => { loadFines(); }, [loadFines]);
    useEffect(() => onRefreshTick(loadFines), [onRefreshTick, loadFines]);

    const selectPeriod = (p) => { setActivePeriod(p.label); setDateFrom(p.from); setDateTo(p.to); };

    // ── Итого ─────────────────────────────────────────────────────
    const totals = useMemo(() => {
        const active   = fines.filter(f => f.is_active);
        const fineList = active.filter(f => !f.is_deduction);
        const dedList  = active.filter(f => f.is_deduction);
        const fineSum  = fineList.reduce((s, f) => s + parseFloat(f.value || 0), 0);
        const dedSum   = dedList.reduce((s, f) => s + parseFloat(f.value || 0), 0);
        return { total: fineSum + dedSum, fineSum, dedSum, fineCount: fineList.length, dedCount: dedList.length };
    }, [fines]);

    // ── Группировка по дате ───────────────────────────────────────
    const grouped = useMemo(() => {
        const map = {};
        // Показываем только активные
        const active = fines.filter(f => f.is_active);
        const sorted = [...active].sort((a, b) => (b.date || '').localeCompare(a.date || '') || b.id - a.id);
        for (const f of sorted) {
            const key = f.date || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(f);
        }
        return Object.entries(map);
    }, [fines]);

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Мои штрафы
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Штрафы и списания
                        </Typography>
                    </Box>
                </Box>

                {/* ── Быстрый выбор периода ────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', gap: 0.8, mb: 2 }}>
                        {periods.map(p => (
                            <Chip key={p.label} size="sm"
                                variant={activePeriod === p.label ? 'solid' : 'outlined'}
                                color={activePeriod === p.label ? 'primary' : 'neutral'}
                                onClick={() => selectPeriod(p)}
                                sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, transition: 'all .15s', px: 2, py: 0.5, ...inputSx }}
                            >
                                {p.label}
                            </Chip>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                        <Input type="date" size="sm" value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                            sx={inputSx}
                        />
                        <Input type="date" size="sm" value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                            sx={inputSx}
                        />
                    </Box>
                </motion.div>

                {/* ── Итого-карточки ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1, mb: 2.5 }}>
                        <Box sx={{
                            gridColumn: '1 / -1', px: 2, py: 1.8,
                            borderRadius: 'var(--joy-radius-lg)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'rgba(239,68,68,0.06)',
                        }}>
                            <Box sx={{ color: '#ef4444', mb: 0.5 }}>
                                <GavelRoundedIcon sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#ef4444', lineHeight: 1 }}>
                                {fmtMoney(totals.total)} ₽
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>
                                Всего за период
                            </Typography>
                        </Box>

                        <Box sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: 'rgba(239,68,68,0.06)' }}>
                            <Box sx={{ color: '#ef4444', mb: 0.3 }}><BlockRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#ef4444', lineHeight: 1 }}>{fmtMoney(totals.fineSum)}</Typography>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>Штрафы · {totals.fineCount}</Typography>
                        </Box>

                        <Box sx={{ px: 2, py: 1.4, borderRadius: 'var(--joy-radius-lg)', border: '1px solid var(--joy-palette-divider)', bgcolor: 'rgba(245,158,11,0.06)' }}>
                            <Box sx={{ color: '#f59e0b', mb: 0.3 }}><MoneyOffRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#f59e0b', lineHeight: 1 }}>{fmtMoney(totals.dedSum)}</Typography>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>Списания · {totals.dedCount}</Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Список ───────────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : grouped.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <GavelRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            За выбранный период нет штрафов, так держать 👏
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1.5}>
                            {grouped.map(([date, items]) => {
                                const dayTotal = items.reduce((s, f) => s + parseFloat(f.value || 0), 0);
                                return (
                                    <motion.div key={date} variants={fadeUp}>
                                        <Box sx={{
                                            bgcolor: 'var(--joy-palette-background-level1)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            borderRadius: 'var(--joy-radius-xl)',
                                            overflow: 'hidden',
                                        }}>
                                            {/* Заголовок дня */}
                                            <Box sx={{
                                                px: 2, py: 1,
                                                borderBottom: '1px solid var(--joy-palette-divider)',
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                    <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {fmtDate(date)}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>
                                                    {fmtMoney(dayTotal)} ₽
                                                </Typography>
                                            </Box>

                                            {/* Строки */}
                                            {items.map((fine, idx) => (
                                                <Box key={fine.id} sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                                    px: 2, py: 1.2,
                                                    borderBottom: idx < items.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                                }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                            {fine.reason}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.2 }}>
                                                            {fine.location_name}
                                                        </Typography>
                                                        {fine.created_by_name && (
                                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                                                Выставил: {fine.created_by_name}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                                        {fine.is_administrator && (
                                                            <Chip size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 700, bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b' }}
                                                                startDecorator={<AdminPanelSettingsRoundedIcon sx={{ fontSize: 12 }} />}
                                                            >
                                                                Админ
                                                            </Chip>
                                                        )}
                                                        <Chip size="sm" variant="soft" sx={{
                                                            fontSize: 10, fontWeight: 700,
                                                            bgcolor: fine.is_deduction ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.10)',
                                                            color: fine.is_deduction ? '#f59e0b' : '#ef4444',
                                                        }}>
                                                            {fine.is_deduction ? 'Списание' : 'Штраф'}
                                                        </Chip>
                                                    </Box>

                                                    <Typography sx={{
                                                        fontSize: 15, fontWeight: 800,
                                                        color: fine.is_deduction ? '#f59e0b' : '#ef4444',
                                                        minWidth: 55, textAlign: 'right',
                                                        fontVariantNumeric: 'tabular-nums',
                                                    }}>
                                                        -{fmtMoney(fine.value)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </motion.div>
                                );
                            })}
                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}